import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { LayoutProvider } from '@app/routing/LayoutContext'
import { LayoutFull } from '@app/routing/LayoutFull'
import { type RouteConfig } from '@app/routing/RenderRoutes'
import { getMatchedRouteLayout } from '@app/routing/getMatchedRouteLayout'

import { SidebarProvider } from '@context/SidebarContext'

import {
  resolveLayoutDefinition,
  useDefaultLayoutPreset,
  type LayoutConfig,
} from '@hooks/useLayoutVariant'

import { LayoutMinimalAnon } from './LayoutMinimalAnon'
import { LayoutMinimalAuth } from './LayoutMinimalAuth'

interface LayoutResolverProps {
  routeConfigs?: RouteConfig<string>[]
  setCurrentPath: (path: string) => void
}

/**
 * layout resolver — reads the default layout preset and renders the matching layout
 * it wraps everything in SidebarProvider so all children can access sidebar state- it's important to keep it as it is!
 *
 * Layout config:
 * - shell   --> minimal-anon | minimal-auth | full
 * - menu    --> visible | hidden
 * - content --> container | fluid | flush
 */
export const LayoutResolver: React.FC<LayoutResolverProps> = ({ routeConfigs, setCurrentPath }) => {
  const { pathname } = useLocation()
  const basePreset = useDefaultLayoutPreset()
  const routeLayout = routeConfigs ? getMatchedRouteLayout(routeConfigs, pathname) : undefined
  const layout = useMemo(
    () => resolveLayoutDefinition(basePreset, routeLayout),
    [basePreset, routeLayout]
  )

  return (
    <SidebarProvider>
      <LayoutResolverInner layout={layout} setCurrentPath={setCurrentPath} />
    </SidebarProvider>
  )
}

const LayoutResolverInner: React.FC<LayoutResolverProps & { layout: LayoutConfig }> = ({
  layout,
  setCurrentPath,
}) => {
  return (
    <LayoutProvider layout={layout}>
      {(() => {
        switch (layout.shell) {
          case 'minimal-anon':
            return <LayoutMinimalAnon layout={layout} setCurrentPath={setCurrentPath} />
          case 'minimal-auth':
            return <LayoutMinimalAuth layout={layout} setCurrentPath={setCurrentPath} />
          case 'full':
            return <LayoutFull layout={layout} setCurrentPath={setCurrentPath} />
          default:
            return null
        }
      })()}
    </LayoutProvider>
  )
}
